import { Box, MenuItem, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import { DateFieldInput } from "modules/record/shared/fields/DateFieldInput";
import { DateTimeFieldInput } from "modules/record/shared/fields/DateTimeFieldInput";

type RelativeUnit = "minute" | "hour" | "day" | "week" | "month" | "year";

interface RelativeLastValue {
    amount?: number;
    unit?: RelativeUnit;
}

interface TemporalConditionValueFieldProps {
    columnType: string;
    operator?: string;
    value: any;
    onChange: (value: any) => void;
    disabled?: boolean;
}

const RELATIVE_UNITS: Array<{ value: RelativeUnit; label: string }> = [
    { value: "minute", label: "minutes" },
    { value: "hour", label: "hours" },
    { value: "day", label: "days" },
    { value: "week", label: "weeks" },
    { value: "month", label: "months" },
    { value: "year", label: "years" },
];

const relativeUnitsForColumnType = (columnType: string) =>
    columnType === "DATE"
        ? RELATIVE_UNITS.filter((unit) => ["day", "week", "month", "year"].includes(unit.value))
        : RELATIVE_UNITS;

const normalizeRelativeLastValue = (
    value: any,
    units: Array<{ value: RelativeUnit; label: string }>
): Required<RelativeLastValue> => ({
    amount: Math.max(1, Number(value?.amount) || 1),
    unit: units.some((unit) => unit.value === value?.unit) ? value.unit : units[0].value,
});

export const TemporalConditionValueField = ({
    columnType,
    operator,
    value,
    onChange,
    disabled = false,
}: TemporalConditionValueFieldProps) => {
    const { t } = useTranslation();

    if (operator === "relative_last" || operator === "relative_next") {
        const units = relativeUnitsForColumnType(columnType);
        const relativeValue = normalizeRelativeLastValue(value, units);

        return (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: "100%" }}>
                <TextField
                    size="small"
                    type="number"
                    value={relativeValue.amount}
                    disabled={disabled}
                    inputProps={{ min: 1, step: 1 }}
                    aria-label={String(t("fluss.temporal.amount", { defaultValue: "Quantidade" }))}
                    onChange={(event) => {
                        onChange({
                            ...relativeValue,
                            amount: Math.max(1, Number(event.target.value) || 1),
                        });
                    }}
                    sx={{ width: { xs: "100%", sm: 96 } }}
                />
                <TextField
                    select
                    size="small"
                    value={relativeValue.unit}
                    disabled={disabled}
                    inputProps={{ "aria-label": String(t("fluss.temporal.unit", { defaultValue: "Unidade" })) }}
                    onChange={(event) => {
                        onChange({
                            ...relativeValue,
                            unit: event.target.value as RelativeUnit,
                        });
                    }}
                    sx={{ flex: 1, minWidth: 140 }}
                >
                    {units.map((unit) => (
                        <MenuItem key={unit.value} value={unit.value}>
                            {t(unit.label)}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
        );
    }

    if (operator === "between") {
        const bounds = Array.isArray(value) ? value : [null, null];
        const Input = columnType === "DATE" ? DateFieldInput : DateTimeFieldInput;
        return <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: "100%" }}>
            {[0, 1].map((index) => <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
                <Input value={typeof bounds[index] === "string" ? bounds[index] : null}
                    onChange={(next) => { const updated = [...bounds]; updated[index] = next; onChange(updated); }}
                    disabled={disabled} />
            </Box>)}
        </Stack>;
    }

    if (columnType === "DATETIME" && (operator === "on" || operator === "not_on")) {
        return (
            <Box sx={{ width: "100%" }}>
                <DateFieldInput
                    value={typeof value === "string" ? value : null}
                    onChange={onChange}
                    disabled={disabled}
                />
            </Box>
        );
    }

    if (columnType === "DATE") {
        return (
            <Box sx={{ width: "100%" }}>
                <DateFieldInput
                    value={typeof value === "string" ? value : null}
                    onChange={onChange}
                    disabled={disabled}
                />
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            <DateTimeFieldInput
                value={typeof value === "string" ? value : null}
                onChange={onChange}
                disabled={disabled}
            />
        </Box>
    );
};
